import { FunctionComponent, useCallback, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import translate from '@i18n'

import { desktopCSS, mobileCSS } from '@measures/responsive'
import { SportEntitySubscription } from '@hooks/useSportEntitySubscriptions'
import Blink from '@components/Blink'
import IconButton from '@components/Buttons/IconButton'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'
import Swiper, { ArrowsVisibilityType, SwiperClass } from '@components/Swiper'

import TeamLogo from '@components/LayoutTabs/SubsectionReusableComponents/TeamLogo'

import BackgroundImage from './BackgroundImage'
import SwiperArrows from './SwiperArrows'

export interface FollowedTeamsListProps {
  items: SportEntitySubscription[]
  onAddTeamClick: () => void
  onItemClick: () => void
}

const Wrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    height: 100%;
    display: flex;
    flex-direction: row;
    gap: ${spacing8};
    box-sizing: border-box;

    align-items: center;
    overflow: auto;
    flex: 1;
  `}
`

const StyledSwiper = styled(Swiper)`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    > .swiper-items-container {
      align-items: center;

      > *:first-child {
        margin-left: ${spacing8};
        scroll-margin-left: ${spacing8};
      }
      > *:nth-last-child(-n + 2) {
        margin-right: 81px;
        scroll-margin-right: 81px;
      }
    }
  `}
`

const StyledSwiperArrows = styled(SwiperArrows)<{
  itemsCount: number
}>`
  ${({ itemsCount }) => css`
    ${itemsCount <= 3 &&
    css`
      display: none;
    `}
  `}
`

const ContentWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    position: relative;
    height: 100%;
    width: 100%;

    gap: ${spacing8};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  `}
`

const TeamItem = styled(Blink)`
  ${({
    theme: {
      spacing: { spacing8, spacing16 },
    },
  }) => css`
    height: 48px;
    min-width: 120px;
    background: linear-gradient(
      180deg,
      rgba(18, 16, 16, 0.48) 0%,
      rgba(18, 16, 16, 0) 86.27%
    );
    padding: ${spacing8} ${spacing16};
    border-radius: 50px;

    box-sizing: border-box;
    appearance: none;
    text-decoration: none;

    position: relative;
    overflow: hidden;
  `}
`

const TeamName = styled.div`
  ${({
    theme: {
      colors: { staticWhite },
      typography: {
        headings: {
          xxsmall: { bundledCSS: headingXXSmallCSS },
        },
      },
    },
  }) => css`
    ${headingXXSmallCSS}
    color: ${staticWhite};
    display: none;

    ${desktopCSS(css`
      display: block;
    `)}
  `}
`

const TeamAbbreviation = styled.div`
  ${({
    theme: {
      colors: { staticWhite },
      typography: {
        headings: {
          xxsmall: { bundledCSS: headingXXSmallCSS },
        },
      },
    },
  }) => css`
    ${headingXXSmallCSS}
    color: ${staticWhite};

    ${desktopCSS(css`
      display: none;
    `)}
  `}
`

const StyledIconButton = styled(IconButton)`
  ${({
    theme: {
      colors: { iconDefault },
    },
  }) => css`
    border-radius: 50%;
    border: 1px solid ${iconDefault};
    display: none;

    ${mobileCSS(css`
      display: block;
    `)}
  `}
`

const StyledSecondaryCTAButton = styled(SecondaryCTAButton)`
  ${mobileCSS(css`
    display: none;
  `)}
`

const FollowedTeamsList: FunctionComponent<FollowedTeamsListProps> = ({
  items,
  onAddTeamClick,
  onItemClick,
}) => {
  const swiperRef = useRef<SwiperClass | null>(null)

  const [arrowsVisibility, setArrowsVisibility] =
    useState<ArrowsVisibilityType>()

  const onInit = useCallback(
    (swiper: any) => {
      if (swiperRef) {
        swiperRef.current = swiper
      }
    },
    [swiperRef]
  )

  const onArrowsVisibility = useCallback((arrow: ArrowsVisibilityType) => {
    setArrowsVisibility(arrow)
  }, [])

  return (
    <Wrapper>
      <StyledSwiper
        initialSlide={0}
        spaceTopBottom={0}
        slidesPerGroup={1}
        spaceBetween={8}
        onInit={onInit}
        onArrowsVisibility={onArrowsVisibility}
        slides={[
          ...items.map((subscription) => (
            <TeamItem
              key={subscription.entityId}
              {...subscription.link}
              onClick={onItemClick}>
              <BackgroundImage image={subscription.backgroundImage}>
                <ContentWrapper>
                  <TeamLogo
                    url={subscription.image.src}
                    name={subscription.image.alt}
                    size={32}
                  />
                  <TeamAbbreviation>
                    {subscription.abbreviation}
                  </TeamAbbreviation>
                  <TeamName>{subscription.title}</TeamName>
                </ContentWrapper>
              </BackgroundImage>
            </TeamItem>
          )),
          <StyledIconButton
            key="addIcon"
            iconName="plus"
            iconSize={24}
            buttonSize={48}
            onClick={onAddTeamClick}
            ariaLabel="Add team"
          />,
          <StyledSecondaryCTAButton
            key="addButton"
            iconName="plus-circle"
            colorVariant="black"
            iconLeft={true}
            onClick={onAddTeamClick}>
            {translate('followMyTeamOverview.addTeam')}
          </StyledSecondaryCTAButton>,
        ]}>
        {arrowsVisibility && arrowsVisibility !== 'none' && (
          <StyledSwiperArrows
            swiperRef={swiperRef}
            arrowsVisibility={arrowsVisibility}
            itemsCount={items?.length ?? 0}
          />
        )}
      </StyledSwiper>
    </Wrapper>
  )
}

export default FollowedTeamsList
